import {List, ListItem, Stack} from '@mui/material';
import React from 'react';

import {ANSText, HapNdiLogo, ProductLogo} from 'presentation/components';
import {ANSTextEnum} from 'presentation/components/ANSText/constants';
import {DateFormatEnum, FeaturesEnum} from 'presentation/constants';
import FEATURE_FLAG_COMPONENTS from 'presentation/constants/featureFlagComponents';
import {useContractStatus, useHostName} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useAccountInformation, useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {stringfyDate} from 'presentation/utils';
import {formatDateString} from '../../utils';
import {CopyButton} from '../CopyButton';
import {WaitingPeriodAlert} from '../WaitingPeriodAlert';
import {StyledCard, StyledListItemText, StyledListSubheader} from './styles';
import {CardDetailsProps} from './types';

const {
	beneficiaryInfo,
	planInfo,
	adherence,
	ansPlanRegister,
	birthday,
	cns,
	company,
	hiredCompany,
	name,
	partialCoverage,
	plan,
	cardCode,
	registration,
	civilName,
	socialName,
	accomodationType,
	network,
	cancellation,
	segmentation,
} = translations['pt-br'].privateFlow.healthCard.cardData;

export function CardDetails({data}: CardDetailsProps) {
	const {
		cdUsuarioC,
		dtCpt,
		nomeEmpresa,
		nuCNS,
		nuMatriculaEmpresa,
		dtAdesaoC,
		nmPlano,
		nmOpeContratada,
		nmSocialUsuarioC,
		nmUsuarioC,
		dtNascimentoUsuarioC,
		flPlanoIntegrado,
		tipoAcomodacaoPlano,
		redeCarteirinha,
		registroAnsSemMascara,
		segmentacaoPlano,
	} = data;
	const {dataHostNameState, isClinipam, isGndiSaoPaulo} = useHostName();
	const {product} = dataHostNameState;
	const {isValid, contractCancellationDate} = useContractStatus();
	const {isTablet} = useMediaQueries();
	const {isInGracePeriod} = useAccountInformation();
	const {isEnabled: isQuickWinWaitingPeriodEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_QUICK_WIN_WAITING_PERIOD,
	);

	const showWaitingPeriodAlert = isInGracePeriod && isQuickWinWaitingPeriodEnabled;

	const customLogoCardDetailsHeight = () => {
		if (isClinipam) return 53;
		if (isGndiSaoPaulo) return 50;
		return 32;
	};

	return (
		<StyledCard elevation={0}>
			{showWaitingPeriodAlert ? <WaitingPeriodAlert /> : null}

			<List subheader={<StyledListSubheader>{planInfo}</StyledListSubheader>}>
				<ListItem disableGutters divider>
					<Stack direction="row" justifyContent="space-between" flex={1}>
						<StyledListItemText
							primary={adherence}
							secondary={!dtAdesaoC ? '-' : dtAdesaoC}
						/>
						{isTablet ? null : (
							<StyledListItemText
								primary={partialCoverage}
								secondary={formatDateString(dtCpt)}
							/>
						)}
					</Stack>
				</ListItem>
				{isTablet ? (
					<ListItem disableGutters divider>
						<StyledListItemText
							primary={partialCoverage}
							secondary={formatDateString(dtCpt)}
						/>
					</ListItem>
				) : null}
				{!isValid ? (
					<ListItem disableGutters divider>
						<Stack direction="row" justifyContent="space-between" flex={1}>
							<StyledListItemText
								primary={cancellation}
								secondary={
									contractCancellationDate
										? stringfyDate(
												contractCancellationDate,
												DateFormatEnum.DATE_FORMAT_8,
											)
										: '-'
								}
							/>
						</Stack>
					</ListItem>
				) : null}
				<ListItem disableGutters divider>
					<StyledListItemText
						primary={company}
						secondary={!nomeEmpresa ? '-' : nomeEmpresa}
					/>
				</ListItem>
				<ListItem disableGutters divider>
					<StyledListItemText primary={plan} secondary={!nmPlano ? '-' : nmPlano} />
				</ListItem>
				<ListItem disableGutters divider>
					<StyledListItemText
						primary={network}
						secondary={!redeCarteirinha ? '-' : redeCarteirinha}
					/>
				</ListItem>
				<ListItem disableGutters divider>
					<StyledListItemText
						primary={cardCode}
						secondary={!cdUsuarioC ? '-' : cdUsuarioC}
					/>
					<CopyButton value={cdUsuarioC || ''} />
				</ListItem>
				<ListItem disableGutters divider>
					<StyledListItemText
						secondaryTypographyProps={{textTransform: 'uppercase'}}
						primary={accomodationType}
						secondary={!tipoAcomodacaoPlano ? '-' : tipoAcomodacaoPlano}
					/>
				</ListItem>
				<ListItem disableGutters divider>
					<StyledListItemText
						secondaryTypographyProps={{textTransform: 'uppercase'}}
						primary={hiredCompany}
						secondary={!nmOpeContratada ? '-' : nmOpeContratada}
					/>
				</ListItem>
				<ListItem disableGutters divider={FEATURE_FLAG_COMPONENTS.healthCard.segmentation}>
					<StyledListItemText
						primary={ansPlanRegister}
						secondary={!registroAnsSemMascara ? '-' : registroAnsSemMascara}
					/>
				</ListItem>
				{FEATURE_FLAG_COMPONENTS.healthCard.segmentation ? (
					<ListItem disableGutters>
						<StyledListItemText
							primary={segmentation}
							secondary={!segmentacaoPlano ? '-' : segmentacaoPlano}
						/>
					</ListItem>
				) : null}
			</List>
			<List subheader={<StyledListSubheader>{beneficiaryInfo}</StyledListSubheader>}>
				{nmSocialUsuarioC ? (
					<ListItem disableGutters divider>
						<StyledListItemText
							secondaryTypographyProps={{textTransform: 'uppercase'}}
							primary={socialName}
							secondary={nmSocialUsuarioC}
						/>
					</ListItem>
				) : null}
				<ListItem disableGutters divider>
					<StyledListItemText
						secondaryTypographyProps={{textTransform: 'uppercase'}}
						primary={nmSocialUsuarioC ? civilName : name}
						secondary={!nmUsuarioC ? '-' : nmUsuarioC}
					/>
				</ListItem>
				<ListItem disableGutters divider>
					<Stack direction="row" justifyContent="space-between" flex={1}>
						<StyledListItemText
							primary={birthday}
							secondary={!dtNascimentoUsuarioC ? '-' : dtNascimentoUsuarioC}
						/>
						<StyledListItemText
							primary={registration}
							secondary={!nuMatriculaEmpresa ? '-' : nuMatriculaEmpresa}
						/>
					</Stack>
				</ListItem>
				<ListItem disableGutters>
					<StyledListItemText primary={cns} secondary={!nuCNS ? '-' : nuCNS} />
				</ListItem>
			</List>
			<Stack alignItems="center" gap={flPlanoIntegrado ? 2 : 1} mt={3} mb={6}>
				{flPlanoIntegrado ? (
					<HapNdiLogo />
				) : (
					<ProductLogo customHeight={customLogoCardDetailsHeight()} />
				)}
				<ANSText operator={flPlanoIntegrado ? ANSTextEnum.ALL : product} />
			</Stack>
		</StyledCard>
	);
}
